/**
 * AddBuildingLl87DiligencePanel: FR-26 Add-Building LL87 diligence, shown
 * AFTER the CollectingFrame warmup (the one permitted live-call path, §7).
 * Render-only: it reads the DB-first `codes.ll87` FR-12 result the
 * building-codes endpoint already ships (PortalBuildingCodesView). Tier-1
 * pitfall #4, the obligation runs with the building and buyers inherit
 * five-figure accruals.
 *
 *   - "Next LL87 due <year> (deadline Dec 31, <year>)", or the digit-5
 *     2026-03-31 override. The BE deadline string is authoritative.
 *   - "Filed <year> per NYC records", never "accepted", which would imply a
 *     DOB acceptance receipt (R3-S4), OR the posting-delay-framed "No LL87
 *     filing on NYC's public record yet …" (deadline-math directive, Leo
 *     2026-07-17), never a "public data through <year>" cutoff claim.
 *   - `evidence_caveats` machine tokens through the owner-copy dictionary
 *     (`ll87Status.ts`); tokens NEVER appear verbatim (FR-26 acceptance).
 *
 * DRY: every value comes from `buildLl87Diligence` / `readLl87WireFields` in
 * `ll87Status.ts`. This panel never re-parses the payload or re-derives the
 * due-year / filing prose.
 *
 * Copy rule 8 (estimated-vs-booked, never owed/billed; confirm-first): NO
 * dollar figures here. The accruing-exposure conversation lives on the building
 * page behind its evidence-tier gating.
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import MonoLabel from "@/components/design/MonoLabel";
import { usePortalBuildingCodesQuery } from "@/queries/portalQueries";
import {
  readLl87WireFields,
  buildLl87Diligence,
  type Ll87Diligence,
} from "@/components/portal/ll87Status";

export interface AddBuildingLl87DiligencePanelProps {
  /** UUID of the building just added; drives the DB-first codes read. */
  buildingId: string;
  /** Fired when the user dismisses the diligence surface. */
  onDone: () => void;
  /** Stable test selector. */
  testId?: string;
}

/** A single labelled diligence line (icon dot + label). */
const DiligenceLine: React.FC<{
  tone: "info" | "muted" | "warn";
  children: React.ReactNode;
  testId?: string;
}> = ({ tone, children, testId }) => (
  <div className="flex items-start gap-2.5 py-2" data-testid={testId}>
    <span
      aria-hidden="true"
      className={cn(
        "mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
        tone === "info" && "bg-teal",
        tone === "muted" && "bg-ink-mute",
        tone === "warn" && "bg-ink",
      )}
    />
    <span className="text-sm text-ink-body leading-snug">{children}</span>
  </div>
);

/** Presentational body, split out so the render test can drive it with a plain
 *  `Ll87Diligence` value without mounting react-query. */
export const Ll87DiligenceBody: React.FC<{
  diligence: Ll87Diligence;
  testId: string;
}> = ({ diligence, testId }) => {
  const { covered, coverageLabel, dueLabel, filingLabel, caveats } = diligence;

  return (
    <div className="space-y-1" data-testid={`${testId}-body`}>
      <DiligenceLine
        tone={covered === false ? "muted" : "info"}
        testId={`${testId}-coverage`}
      >
        {coverageLabel}
      </DiligenceLine>

      {dueLabel ? (
        <DiligenceLine tone="info" testId={`${testId}-due`}>
          {dueLabel}
        </DiligenceLine>
      ) : null}

      {filingLabel ? (
        <DiligenceLine
          tone={filingLabel.startsWith("Filed") ? "info" : "warn"}
          testId={`${testId}-filing`}
        >
          {filingLabel}
        </DiligenceLine>
      ) : null}

      {caveats.length > 0 ? (
        <ul
          className="mt-2 space-y-1 border-t border-ink-line pt-3"
          data-testid={`${testId}-caveats`}
        >
          {caveats.map((copy) => (
            <li
              key={copy}
              className="text-[11px] leading-[1.5] text-ink-mute"
              data-testid={`${testId}-caveat`}
            >
              {copy}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export const AddBuildingLl87DiligencePanel: React.FC<
  AddBuildingLl87DiligencePanelProps
> = ({ buildingId, onDone, testId = "ll87-diligence" }) => {
  // DB-first read of the FR-12 result the warmup already persisted.
  // Best-effort: a miss (query error, or LL87 absent because the building isn't
  // carbon-scoped) hides the panel body only. "Done" is always live.
  const { data, isLoading } = usePortalBuildingCodesQuery(buildingId);

  const ll87Code = data?.codes?.ll87;
  const diligence = ll87Code
    ? buildLl87Diligence(readLl87WireFields(ll87Code))
    : null;

  // Body branches, extracted to avoid a nested ternary in JSX: diligence
  // present → the FR-26 lines; loading → a "checking…" line; resolved but
  // absent → graceful fallback, never a dead end.
  let bodyContent: React.ReactNode;
  if (diligence) {
    bodyContent = <Ll87DiligenceBody diligence={diligence} testId={testId} />;
  } else if (isLoading) {
    bodyContent = (
      <p className="text-sm text-ink-mute" data-testid={`${testId}-loading`}>
        Checking LL87 coverage and filing history…
      </p>
    );
  } else {
    bodyContent = (
      <p
        className="text-sm text-ink-mute"
        data-testid={`${testId}-unavailable`}
      >
        We&rsquo;ll surface this building&rsquo;s LL87 status on its building
        page once its records finish syncing.
      </p>
    );
  }

  return (
    <div
      className="space-y-6"
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <header className="space-y-2">
        <MonoLabel className="mb-1">Building added · LL87 diligence</MonoLabel>
        <h2
          className="font-display text-2xl font-medium text-ink leading-tight"
          data-testid={`${testId}-headline`}
        >
          Energy Efficiency Report (LL87)
        </h2>
        <p className="text-sm text-ink-mute leading-snug">
          What NYC records show for this building today. The obligation runs
          with the building — worth knowing before you rely on it.
        </p>
      </header>

      {bodyContent}

      <div className="pt-2">
        <button
          type="button"
          onClick={onDone}
          data-testid={`${testId}-done`}
          className="block w-full text-center bg-teal text-paper px-0 py-[13px] rounded-lg font-semibold text-sm hover:bg-teal/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal/40"
        >
          Done
        </button>
      </div>
    </div>
  );
};

export default AddBuildingLl87DiligencePanel;
